import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { ChevronRight, Send, X as XIcon, LogIn } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Pitch-your-event call to action card + inline expansion form.
 *
 * Submits to `/api/event-pitches` so pitches land in the Admin panel
 * (parallel to Suggestions / Contact Messages), not in someone's email inbox.
 *
 * Unauthed visitors see a CTA to log in instead.
 */
const EMPTY = {
  title: "",
  description: "",
  proposed_date: "",
  location_type: "either",
  audience: "",
  co_host: false,
};

const EventPitchCTA = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/event-pitches`, form);
      toast.success("Your pitch is with the team — thank you!");
      setForm(EMPTY);
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send your pitch");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="pb-16" data-testid="pitch-event-section">
      <div className="container-main">
        {!open ? (
          <div
            className="bg-white border-2 border-[#0A0A0A] p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 shadow-[6px_6px_0_#FFC2E8]"
            data-testid="pitch-event-cta"
          >
            <div className="flex-1">
              <p className="overline text-[#FF2D87] mb-1">Got an idea?</p>
              <h3 className="text-xl md:text-2xl font-display font-bold leading-tight">
                Pitch a workshop or meetup
              </h3>
              <p className="text-sm text-[#4A4A4A] mt-1">
                Have an idea you want to share? We'd love to hear it. Your pitch goes straight to the events team.
              </p>
            </div>
            {user ? (
              <button
                onClick={() => setOpen(true)}
                className="btn-primary inline-flex items-center gap-2 whitespace-nowrap"
                data-testid="pitch-event-open"
              >
                Pitch your event <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <Link
                to="/login"
                className="btn-primary inline-flex items-center gap-2 whitespace-nowrap"
                data-testid="pitch-event-login"
              >
                <LogIn className="w-4 h-4" /> Log in to pitch
              </Link>
            )}
          </div>
        ) : (
          <form
            onSubmit={submit}
            className="bg-white border-2 border-[#0A0A0A] p-6 md:p-8 shadow-[6px_6px_0_#FFC2E8] space-y-4"
            data-testid="pitch-event-form"
          >
            <div className="flex items-start justify-between gap-4 mb-2">
              <div>
                <p className="overline text-[#FF2D87] mb-1">Pitch a workshop or meetup</p>
                <h3 className="text-xl md:text-2xl font-display font-bold leading-tight">
                  Tell us what you have in mind
                </h3>
                <p className="text-xs text-[#4A4A4A] mt-1">
                  Submissions land privately in the admin panel — we'll get back to you at <strong>{user?.email}</strong>.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-[#4A4A4A] hover:text-[#0A0A0A]"
                title="Cancel"
                data-testid="pitch-event-close"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </div>

            <div>
              <label className="form-label">Event title <span className="text-[#FF2D87]">*</span></label>
              <input
                type="text"
                required
                maxLength={200}
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Brand Photography 101"
                className="w-full px-4 py-3 border-2 border-[#0A0A0A] bg-white focus:outline-none focus:border-[#FF2D87] text-sm"
                data-testid="pitch-title"
              />
            </div>

            <div>
              <label className="form-label">What's it about? <span className="text-[#FF2D87]">*</span></label>
              <textarea
                required
                rows={5}
                maxLength={5000}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="What members will learn or experience, format, length, anything we should know…"
                className="w-full px-4 py-3 border-2 border-[#0A0A0A] bg-white focus:outline-none focus:border-[#FF2D87] text-sm resize-y"
                data-testid="pitch-description"
              />
              <p className="text-[10px] text-[#4A4A4A] mt-1">{form.description.length}/5000</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="form-label">Proposed date / window</label>
                <input
                  type="text"
                  maxLength={80}
                  value={form.proposed_date}
                  onChange={(e) => setForm({ ...form, proposed_date: e.target.value })}
                  placeholder="e.g. Mid-June, weekday evening"
                  className="w-full px-4 py-3 border-2 border-[#0A0A0A] bg-white focus:outline-none focus:border-[#FF2D87] text-sm"
                  data-testid="pitch-date"
                />
              </div>
              <div>
                <label className="form-label">Format</label>
                <select
                  value={form.location_type}
                  onChange={(e) => setForm({ ...form, location_type: e.target.value })}
                  className="w-full px-4 py-3 border-2 border-[#0A0A0A] bg-white focus:outline-none focus:border-[#FF2D87] text-sm"
                  data-testid="pitch-location-type"
                >
                  <option value="either">Either / open</option>
                  <option value="virtual">Virtual</option>
                  <option value="in-person">In-person</option>
                </select>
              </div>
            </div>

            <div>
              <label className="form-label">Who's this for?</label>
              <input
                type="text"
                maxLength={300}
                value={form.audience}
                onChange={(e) => setForm({ ...form, audience: e.target.value })}
                placeholder="e.g. New freelancers, founders in year 1, designers…"
                className="w-full px-4 py-3 border-2 border-[#0A0A0A] bg-white focus:outline-none focus:border-[#FF2D87] text-sm"
                data-testid="pitch-audience"
              />
            </div>

            <label className="flex items-center gap-2 text-sm text-[#0A0A0A] cursor-pointer">
              <input
                type="checkbox"
                checked={form.co_host}
                onChange={(e) => setForm({ ...form, co_host: e.target.checked })}
                className="w-4 h-4 accent-[#FF2D87]"
                data-testid="pitch-cohost"
              />
              I'd love to <strong>co-host</strong> or run this myself
            </label>

            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-xs font-bold uppercase tracking-widest text-[#4A4A4A] hover:text-[#0A0A0A] px-3 py-2"
                data-testid="pitch-cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !form.title.trim() || !form.description.trim()}
                className="btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="pitch-submit"
              >
                <Send className="w-3.5 h-3.5" /> {submitting ? "Sending…" : "Send pitch"}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
};

export default EventPitchCTA;
